import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import MobileBottomNav from '@/app/layouts/MobileBottomNav';
import Sidebar from '@/app/layouts/Sidebar';
import TopBar from '@/app/layouts/TopBar';
import ClassFormModalHost from '@/features/class/manage/ClassFormModalHost';
import ClassShareModalHost from '@/features/class/manage/ClassShareModalHost';
import ClassCategoriesManageModalHost from '@/features/profile/classes/ClassCategoriesManageModalHost';
import ClassCategoryBulkConfirmModalHost from '@/features/profile/classes/ClassCategoryBulkConfirmModalHost';
import ProfileImageCropModalHost from '@/features/profile/edit/crop/ProfileImageCropModalHost';
import ProfileShareModalHost from '@/features/profile/ProfileShareModalHost';
import { fetchUserByIdWithRaw } from '@/features/profile/api';
import { mergeRawAuthUser } from '@/features/auth/mergeRawAuthUser';
import {
  PushToastHost,
  usePushMessageBridge,
  usePushNotifications,
} from '@/features/push-notifications';
import { useAuthStore } from '@/store/authStore';
import { mobileShellMainClassName } from '@/shared/config/theme';
import { APP_PATHS } from '@/app/routes/paths';
import { useBackgroundChatConnection } from '@/features/messages/useBackgroundChatConnection';

export default function AppShellLayout() {
  const { t } = useTranslation('nav');
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMessagesRoute =
    pathname === APP_PATHS.messages || pathname.startsWith(`${APP_PATHS.messages}/`);
  useBackgroundChatConnection(!isMessagesRoute);

  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  // When the app shell mounts and the user is already authenticated (i.e.
  // direct landing on /feed after a hard refresh), refresh the persisted
  // login payload via GET /userservice/api/v3/user/:userId. This keeps
  // header counts (Posts / Following / Followers / Joined classes) in sync
  // without waiting for the user to open their profile page.
  const authUserId = useAuthStore((state) => state.user?.id);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setRawUser = useAuthStore((state) => state.setRawUser);
  useEffect(() => {
    if (!isAuthenticated || !authUserId) return;
    let cancelled = false;
    void fetchUserByIdWithRaw(authUserId)
      .then(({ raw }) => {
        if (!cancelled) {
          const previous = useAuthStore.getState().rawUser;
          setRawUser(mergeRawAuthUser(previous, raw));
        }
      })
      .catch(() => {
        /* silent — counts will fall back to the persisted snapshot. */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authUserId, setRawUser]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Keep the FCM token fresh while the user is signed in. The hook is a
  // silent no-op until the user explicitly enables push via
  // `<PushPermissionPrompt />`, which we render at the top of the home feed
  // (`GlobalFeedView`) so users see it right after login.
  usePushNotifications();

  // Surface every incoming push as an in-app toast AND refresh the bell /
  // notifications screen so the new item appears without a manual reload.
  usePushMessageBridge();

  useEffect(() => {
    if (!mobileNavOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMobileNav();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileNavOpen, closeMobileNav]);

  return (
    <div className="flex h-dvh overflow-hidden bg-gray-100">
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          aria-label={t('closeMenu')}
          onClick={closeMobileNav}
        />
      ) : null}

      <Sidebar
        className={clsx(
          'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-50 max-lg:transition-transform max-lg:duration-200 max-lg:ease-out motion-reduce:max-lg:transition-none lg:static',
          mobileNavOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full',
        )}
        onNavigate={closeMobileNav}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenuClick={openMobileNav} />
        <main className={clsx('min-h-0 flex-1 overflow-hidden', mobileShellMainClassName)}>
          <Outlet />
        </main>
      </div>

      <MobileBottomNav />
      <ClassFormModalHost />
      <ClassShareModalHost />
      <ClassCategoriesManageModalHost />
      <ClassCategoryBulkConfirmModalHost />
      <ProfileShareModalHost />
      <ProfileImageCropModalHost />
      <PushToastHost />
    </div>
  );
}
